import { ChangeEventHandler, FC, useRef } from 'react';

import { InputController } from 'portfolio3/ui-kit/types';
import { detectTouchDevice } from 'utils';
import { mergeSx } from 'utils';

import { UiKitInputBaseProps } from '../../types';
import Input from '../Input';
import NumberInputEndAdornment from './NumberInputEndAdornment';
import * as styles from './styles';

interface INumberInputProps extends UiKitInputBaseProps {
  controller: InputController;
  min?: number;
  max?: number;
  step?: number;
  pattern?: string;
  withControls?: boolean;
  validateOnBlur?: boolean;
}

const NumberInput: FC<INumberInputProps> = ({
  controller,
  min,
  max,
  step,
  withControls = true,
  validateOnBlur,
  sx,
  ...props
}) => {
  const isTouchDevice = detectTouchDevice();
  const inputRef = useRef<HTMLInputElement>(null);
  const stepValue = step || 1;
  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value.replace(/[^\d]/g, '');

    if (validateOnBlur) {
      controller.handleChange(value);
    }

    const valueNumber = Number(value);

    const isInvalidMin = min !== undefined && valueNumber < min;
    const isInvalidMax = max !== undefined && valueNumber > max;

    if (isInvalidMin || isInvalidMax) {
      event.preventDefault();
      return;
    }

    controller.handleChange(value);
  };

  const handleBlur: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    const value = event.target.value.replace(/[^\d]/g, '');

    if (!validateOnBlur || value.length === 0) {
      controller.handleBlur?.(value);
      return;
    }

    const valueNumber = Number(value);

    const isInvalidMin = min !== undefined && valueNumber < min;
    const isInvalidMax = max !== undefined && valueNumber > max;

    let clampedValue = valueNumber;
    if (isInvalidMin) clampedValue = min;
    if (isInvalidMax) clampedValue = max;

    const newValue = String(clampedValue);

    controller.handleChange(newValue);
    controller.handleBlur?.(newValue);
  };

  const handleStepUp = () => {
    const currentValue = inputRef.current?.value;
    const currentNumber = currentValue ? Number(currentValue) : 0;
    const newValue = String(currentNumber + stepValue);

    // Проверка на max
    if (max !== undefined && currentNumber + stepValue > max) {
      return;
    }

    inputRef.current!.value = newValue;
    controller.handleChange(newValue);
  };

  const handleStepDown = () => {
    const currentValue = inputRef.current?.value;
    const currentNumber = currentValue ? Number(currentValue) : 0;
    const newValue = String(currentNumber - stepValue);

    // Проверка на min
    if (min !== undefined && currentNumber - stepValue < min) {
      return;
    }

    inputRef.current!.value = newValue;
    controller.handleChange(newValue);
  };

  const withControlsStyles = withControls ? styles.inputRootWithControls : undefined;

  return (
    <Input
      inputProps={{
        min,
        max,
        step,
      }}
      {...props}
      sx={mergeSx(styles.inputRoot, withControlsStyles, sx)}
      inputRef={inputRef}
      inputMode="numeric"
      controller={controller}
      onChange={handleChange}
      onBlur={handleBlur}
      endAdornment={
        !isTouchDevice &&
        withControls && <NumberInputEndAdornment onStepUp={handleStepUp} onStepDown={handleStepDown} />
      }
    />
  );
};

export default NumberInput;
